import { DEFAULT_DISPLAY_TIMEZONE, todayInTimeZone } from '@/i18n/dates'
import type { Locale } from '@/i18n/locales'

/**
 * The site footer's navigation, built per locale for the global shell (`FooterContainer`).
 *
 * Same split as the navbar's `navigation/menu-config.ts`:
 *
 *   - **Copy** — section titles, link names, the description and copyright line — lives
 *     in the translation store (`src/content/translations/ui/footer.json`) and arrives
 *     through the injected translator, so translators own every visible string.
 *   - **Structure** — which routes the footer promotes and the paths they point at — is
 *     editorial routing data, not translatable prose, so it stays here. Unlike the
 *     navbar's promoted sets it does not vary by locale: the footer lists the site's
 *     evergreen surfaces, which every locale serves.
 */

/** Minimal translator shape — keeps this module React-free and unit-testable. */
export type Translate = (key: string, values?: Record<string, string>) => string

export interface FooterLink {
    name: string
    href: string
}

export interface FooterSection {
    title: string
    links: FooterLink[]
}

/** Prefix a site path with the active locale: `/carrier/` → `/es-mx/carrier/`. */
function localePath(locale: Locale, path: string): string {
    return `/${locale}${path}`
}

/**
 * The blog and the help centre are shared surfaces served outside the locale tree, so
 * they are absolute and carry no locale prefix.
 */
const BLOG_URL = 'https://www.wanderu.com/blog/'
const PRESS_URL = 'https://www.wanderu.com/blog/press/'
const HELP_URL = 'https://help.wanderu.com/'

export function buildFooterSections(locale: Locale, t: Translate): FooterSection[] {
    return [
        {
            title: t('travel'),
            links: [
                { name: t('busTickets'), href: localePath(locale, '/bus-tickets/') },
                { name: t('trainTickets'), href: localePath(locale, '/train-tickets/') },
            ],
        },
        {
            title: t('about'),
            links: [
                { name: t('aboutUs'), href: localePath(locale, '/about-us/') },
                { name: t('blog'), href: BLOG_URL },
                { name: t('press'), href: PRESS_URL },
                { name: t('careers'), href: localePath(locale, '/about-us/careers/') },
            ],
        },
        {
            title: t('customerService'),
            links: [
                { name: t('contactUs'), href: HELP_URL },
                { name: t('companies'), href: localePath(locale, '/carrier/') },
            ],
        },
    ]
}

export function buildFooterLegalLinks(locale: Locale, t: Translate): FooterLink[] {
    return [
        {
            name: t('termsAndConditions'),
            href: localePath(locale, '/terms-and-conditions/'),
        },
        { name: t('privacyPolicy'), href: localePath(locale, '/privacy-policy/') },
    ]
}

/**
 * The copyright year, projected in the app's display timezone rather than read off the
 * runtime's own zone — the footer renders on both sides of hydration, and an unpinned
 * `getFullYear()` disagrees between the server (UTC) and an Eastern client for the last
 * five hours of December 31 (`invariants.md#ssr-safe-rendered-dates`).
 */
export function copyrightYear(): string {
    return todayInTimeZone(DEFAULT_DISPLAY_TIMEZONE).slice(0, 4)
}
